import intersection from 'lodash-es/intersection';
import isNil from 'lodash-es/isNil';
import noop from 'lodash-es/noop';
import { useClient } from '../../ClientProvider';
import { mergeMutationState } from '../../mergeState';
import useMutation from '../../useMutation';
const INCOMPATIBLE_METRICS = ['HPOT', 'U_CAPA', 'ECP'];
export const ALLOWED_DUPLICATED_METRICS_ON_PLOTS = ['HPOT', 'T_CAL'];
/**
 * Checks for conflicts on available measures for the given devices
 * @param devices to check
 */
export const checkForDeviceConflict = (devices) => {
    const devicesByMetrics = {};
    // Building a record of metric -> Device[]
    devices.forEach((currentDevice) => {
        currentDevice.availableMeasures.forEach((m) => {
            if (isNil(devicesByMetrics[m])) {
                devicesByMetrics[m] = [];
            }
            devicesByMetrics[m]?.push(currentDevice);
        });
    });
    // For each metric if the array of device is larger than 1, then there is a duplication conflict
    const duplicationsConflicts = Object.entries(devicesByMetrics).reduce((acc, [metric, devicesWithMetric]) => {
        if (devicesWithMetric.length > 1 &&
            !ALLOWED_DUPLICATED_METRICS_ON_PLOTS.includes(metric)) {
            const castedMetric = metric;
            acc.push({ metric: castedMetric, devices: devicesWithMetric });
        }
        return acc;
    }, []);
    const incompatibleMetrics = intersection(Object.keys(devicesByMetrics), INCOMPATIBLE_METRICS);
    const incompatibilityConflicts = [];
    // If on the given sensors we have an intersection larger than one on the incompatible metrics and the metrics recorded on all the sensors
    // Then we have an incompatibility conflict
    if (incompatibleMetrics.length > 1) {
        incompatibilityConflicts.push({
            metrics: incompatibleMetrics,
            devices: [
                ...(devicesByMetrics['U_CAPA'] ?? []),
                ...(devicesByMetrics['HPOT'] ?? []),
                ...(devicesByMetrics['ECP'] ?? [])
            ]
        });
    }
    return {
        duplications: duplicationsConflicts,
        incompatibilities: incompatibilityConflicts
    };
};
/**
 * Helper method to create an array of DeviceConflict from a DeviceIdsRecord
 */
export const buildConflictsFromIdsRecord = (selectedDeviceIds, devices) => {
    const selectedIds = Object.entries(selectedDeviceIds)
        .filter(([, { isActive }]) => isActive)
        .map(([idAsString]) => parseInt(idAsString, 10));
    const completeSelectedDevice = devices.filter((d) => selectedIds.includes(d.id));
    return checkForDeviceConflict(completeSelectedDevice);
};
/**
 * Hook to update devices linked to a plot
 */
const useUpdateOrgPlotDevices = ({ plotId, deviceIdsRecord, onSuccess, onError = noop }) => {
    const client = useClient();
    const [updateHorizon, updateHorizonRequest] = useMutation(client.measurementsConfig.plots.changeHorizon(), {
        keysToInvalidate: [client.plots.get(plotId).invalidationKey]
    });
    const handleConfigHorizonChange = async ({ measurementConfig, newHorizonId }) => {
        if (!isNil(measurementConfig.deviceId)) {
            await updateHorizon({
                horizonId: newHorizonId,
                deviceId: measurementConfig.deviceId,
                plotId: measurementConfig.plotId
            });
        }
    };
    const [updateDevices, updateDevicesRequest] = useMutation(client.plots.updateDevices(plotId), {
        onSuccess: async (plot) => {
            // Only preserving the configurations matching the new devices ids
            const matchingMeasurementConfig = plot?.currentMeasurementConfigs.reduce((acc, mc) => {
                if (!isNil(mc.deviceId) && mc.metric === 'HPOT') {
                    const sd = deviceIdsRecord[mc.deviceId];
                    if (sd && !isNil(sd.horizonId)) {
                        acc.push({ measurementConfig: mc, newHorizonId: sd.horizonId });
                    }
                }
                return acc;
            }, []);
            const allPromise = matchingMeasurementConfig.map((x) => handleConfigHorizonChange(x));
            await Promise.all(allPromise).then(() => {
                onSuccess();
            });
        },
        onError
    });
    const handlePlotDevicesUpdate = async () => {
        const allDeviceIds = Object.entries(deviceIdsRecord).reduce((acc, [key, value]) => (value.isActive ? acc.concat(parseInt(key, 10)) : acc), []);
        await updateDevices({ deviceIds: allDeviceIds });
    };
    const plotDeviceUpdateState = mergeMutationState(updateDevicesRequest, updateHorizonRequest);
    return {
        handlePlotDevicesUpdate,
        plotDeviceUpdateState
    };
};
export default useUpdateOrgPlotDevices;
